import React from 'react';
import {Alert, Box, Button, Dialog, DialogContent, IconButton, Typography} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

const stepIconSx = {
    width: 56,
    height: 56,
    borderRadius: '16px',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    color: '#19a463',
    backgroundColor: '#e9faf1',
};

function Step({number, icon, children}) {
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: {xs: 1.5, sm: 2}, position: 'relative'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0}}>
                <Box sx={{width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', color: '#159b5c', backgroundColor: '#e1f8eb', fontWeight: 800, fontSize: 15}}>
                    {number}
                </Box>
                <Box sx={stepIconSx}>{icon}</Box>
            </Box>
            <Typography sx={{fontSize: {xs: 15, sm: 17}, fontWeight: 700, lineHeight: 1.35, color: '#172033'}}>
                {children}
            </Typography>
        </Box>
    );
}

export default function TeregoPromotionInfoDialog({open, onClose, onContinue, busy = false, error, translate = false}) {
    const t = (romanian, english) => (translate ? english : romanian);

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="terego-promotion-info-title"
            BackdropProps={{sx: {backgroundColor: 'rgba(15, 23, 42, 0.66)', backdropFilter: 'blur(6px)'}}}
            PaperProps={{sx: {borderRadius: {xs: '24px', sm: '28px'}, m: 2, width: 'calc(100% - 32px)', maxWidth: 590, maxHeight: 'calc(100vh - 32px)', overflow: 'hidden', boxShadow: '0 24px 80px rgba(15, 23, 42, 0.24)', color: '#172033'}}}
        >
            <DialogContent sx={{p: 0, overflowY: 'auto'}}>
                <Box sx={{px: {xs: 2.5, sm: 4}, pt: {xs: 2.5, sm: 3.5}, pb: {xs: 2.5, sm: 3}}}>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                            <Box sx={{width: {xs: 64, sm: 78}, height: {xs: 64, sm: 78}, borderRadius: '50%', display: 'grid', placeItems: 'center', color: '#1ca463', backgroundColor: '#e6faef', flexShrink: 0}}>
                                <Box sx={{position: 'relative', display: 'flex', alignItems: 'center'}}>
                                    <DescriptionOutlinedIcon sx={{fontSize: {xs: 34, sm: 42}}}/>
                                    <DirectionsCarOutlinedIcon sx={{fontSize: {xs: 30, sm: 38}, position: 'absolute', left: {xs: 19, sm: 23}, top: {xs: 20, sm: 25}, backgroundColor: '#e6faef', borderRadius: '50%'}}/>
                                </Box>
                            </Box>
                            <Box>
                                <Typography id="terego-promotion-info-title" sx={{fontSize: {xs: 22, sm: 28}, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.03em'}}>
                                    {t('Cum obții cazierul gratuit?', 'How do you get the criminal record for free?')}
                                </Typography>
                                <Typography sx={{mt: 0.75, color: '#77808f', fontSize: {xs: 14, sm: 15}, lineHeight: 1.45}}>
                                    {t('Prin partenerul nostru Terego.ro, costul cazierului este acoperit pentru tine.', 'Through our partner Terego.ro, the cost of the criminal record is covered for you.')}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton aria-label={t('Închide', 'Close')} onClick={onClose} disabled={busy} size="small" sx={{bgcolor: '#f3f4f6', color: '#77808f', flexShrink: 0}}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>

                    <Box sx={{mt: {xs: 3, sm: 3.5}, display: 'grid', gap: {xs: 2.25, sm: 2.5}}}>
                        <Step number="1" icon={<AssignmentTurnedInOutlinedIcon/>}>
                            {t('Încarci certificatul de înmatriculare sau o poliță RCA actuală', 'Upload the registration certificate or a current RCA policy')}
                        </Step>
                        <Step number="2" icon={<VerifiedUserOutlinedIcon/>}>
                            {t('Terego îți pregătește cea mai bună ofertă RCA pentru mașina ta', 'Terego prepares the best RCA offer for your car')}
                        </Step>
                        <Step number="3" icon={<DescriptionOutlinedIcon/>}>
                            {t('Primești cazierul gratuit când continui prin oferta partenerului', 'You receive the criminal record for free when you continue through the partner offer')}
                        </Step>
                    </Box>

                    <Box sx={{mt: {xs: 2.75, sm: 3}, p: {xs: 1.75, sm: 2}, display: 'flex', gap: 1.25, borderRadius: '16px', backgroundColor: '#eef5ff', color: '#315a9a'}}>
                        <InfoOutlinedIcon sx={{mt: 0.15, flexShrink: 0, color: '#3476d3'}}/>
                        <Box>
                            <Typography sx={{fontSize: 14, fontWeight: 800, lineHeight: 1.35}}>
                                {t('De ce cerem acest document?', 'Why do we need this document?')}
                            </Typography>
                            <Typography sx={{mt: 0.5, fontSize: 13, lineHeight: 1.45, color: '#60708a'}}>
                                {t('Avem nevoie de datele mașinii pentru a-ți calcula oferta RCA și pentru ca partenerul nostru să poată acoperi costul cazierului.', 'We need the car details to calculate your RCA offer and allow our partner to cover the cost of the criminal record.')}
                            </Typography>
                            <Typography sx={{mt: 0.35, fontSize: 13, fontWeight: 800, lineHeight: 1.45, color: '#315a9a'}}>
                                {t('Nu trebuie să plătești acum polița RCA.', 'You do not need to pay for the RCA policy now.')}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{mt: 1.5, p: {xs: 1.5, sm: 1.75}, display: 'flex', alignItems: 'center', gap: 1.25, borderRadius: '14px', backgroundColor: '#e9faef', color: '#208c57'}}>
                        <CheckCircleIcon sx={{flexShrink: 0}}/>
                        <Typography sx={{fontSize: 13, lineHeight: 1.4, fontWeight: 700}}>
                            {t('Simplu, rapid și fără cost pentru cazier — plus un preț avantajos la reînnoirea RCA.', 'Simple, fast and free for the criminal record — plus a great price when renewing your RCA.')}
                        </Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{mt: 1.5}}>{error}</Alert>}

                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        onClick={onContinue}
                        disabled={busy}
                        endIcon={<ArrowForwardIcon/>}
                        sx={{mt: 1.75, minHeight: 52, borderRadius: '12px', textTransform: 'none', fontSize: 17, fontWeight: 800, boxShadow: 'none', '&:hover': {boxShadow: 'none'}}}
                    >
                        {t('Continuă', 'Continue')}
                    </Button>
                    <Typography sx={{mt: 1, textAlign: 'center', color: '#77808f', fontSize: 12}}>
                        {t('Vei încărca documentul la pasul următor.', 'You will upload the document in the next step.')}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
